import { useCallback, useEffect, useRef, useState } from 'react';
import { useTable } from '../components/table';

// Camada canonica de debounce da busca compartilhada das tabelas. Inputs proprios de
// consumers chamam `setSearch` a cada tecla, por isso o atraso vive aqui e nao na toolbar.
export const SEARCH_DEBOUNCE_MS = 400;

const useTableFilter = (
  {
    defaultFilter = {},
    tableProps = {},
    defaultCurrentPage,
    defaultRowsPerPage,
    defaultDense,
    defaultOrderBy,
    defaultOrder,
    defaultSelected,
  } = {
    defaultFilter: {},
    tableProps: {},
  }
) => {
  const [filter, setFilter] = useState(defaultFilter);

  const [showFilter, setShowFilter] = useState(false);

  const [dirtyFilters, setDirtyFilters] = useState(0);

  const [search, setSearchState] = useState('');

  const table = useTable({
    defaultCurrentPage: defaultCurrentPage ?? 1,
    defaultRowsPerPage: defaultRowsPerPage ?? 10,
    ...(defaultDense !== undefined ? { defaultDense } : {}),
    ...(defaultOrderBy !== undefined ? { defaultOrderBy } : {}),
    ...(defaultOrder !== undefined ? { defaultOrder } : {}),
    ...(defaultSelected !== undefined ? { defaultSelected } : {}),
    ...tableProps,
  });

  const { setPage, onSort: sortTable } = table;

  // A página N da ordem antiga não existe na nova: ordenar volta ao começo, como a busca.
  const onSort = useCallback(
    (id) => {
      sortTable(id);
      setPage(1);
    },
    [sortTable, setPage]
  );

  const pendingSearchRef = useRef(null);

  const clearPendingSearch = useCallback(() => {
    if (pendingSearchRef.current) {
      clearTimeout(pendingSearchRef.current);
      pendingSearchRef.current = null;
    }
  }, []);

  const commitSearch = useCallback(
    (value) => {
      setSearchState(value);
      setPage(1);
    },
    [setPage]
  );

  const setSearch = useCallback(
    (value) => {
      const nextSearch = value ?? '';

      clearPendingSearch();

      // Limpar a busca e excecao ao debounce: restaura a listagem sem esperar o intervalo.
      if (nextSearch === '') {
        commitSearch('');
        return;
      }

      pendingSearchRef.current = setTimeout(() => {
        pendingSearchRef.current = null;
        commitSearch(nextSearch);
      }, SEARCH_DEBOUNCE_MS);
    },
    [clearPendingSearch, commitSearch]
  );

  useEffect(() => clearPendingSearch, [clearPendingSearch]);

  return {
    filter,
    setFilter,
    dirtyFilters,
    setDirtyFilters,
    search,
    setSearch,
    showFilter,
    setShowFilter,
    ...table,
    onSort,
  };
};

export default useTableFilter;
